import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Crown, Star, Camera, Check, X } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';

export const TestimonialModal: React.FC = () => {
    const { user, session, subscriptionStatus } = useAuth();
    const { t } = useTranslation();
    const [isVisible, setIsVisible] = useState(false);
    const [isClaiming, setIsClaiming] = useState(false);
    const [stats, setStats] = useState<any>(null);

    // Initial load & Setup listeners for user stats
    useEffect(() => {
        const loadStats = () => {
            try {
                const s = localStorage.getItem('aura_user_data');
                if (s) setStats(JSON.parse(s));
            } catch (e) {}
        };
        loadStats();
        
        const handleStatsUpdated = () => {
             loadStats();
        };

        window.addEventListener('aura_stats_updated', handleStatsUpdated);
        // Fallback for older data flows
        window.addEventListener('aura_sync_complete', handleStatsUpdated);
        
        return () => {
             window.removeEventListener('aura_stats_updated', handleStatsUpdated);
             window.removeEventListener('aura_sync_complete', handleStatsUpdated);
        }
    }, [user]);

    useEffect(() => {
        // Only show if the user has the pending flag, and has a PRO subscription
        if (
            user && 
            ['pro', 'active', 'trialing'].includes(subscriptionStatus || '') && 
            stats?.pending_testimonial_request === true &&
            stats?.testimonial_consent !== 'granted'
        ) {
            // Add a small delay so it doesn't pop up exactly on app render
            const timer = setTimeout(() => setIsVisible(true), 1500);
            return () => clearTimeout(timer);
        } else {
            setIsVisible(false);
        }
    }, [user, subscriptionStatus, stats]);

    const handleAccept = async () => {
        if (!session) return;
        setIsClaiming(true);
        toast.loading(t('testimonial.processing'), { id: 'claim-testimonial' });

        try {
            const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/claim-testimonial-reward`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`
                }
            });

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || t('testimonial.error_claim'));
            }

            toast.dismiss('claim-testimonial');
            toast.success(t('testimonial.success'), { duration: 5000 });
            
            // Update local state and aggressively drop modal
            try {
                const currentStats = JSON.parse(localStorage.getItem('aura_user_data') || '{}');
                currentStats.pending_testimonial_request = false;
                currentStats.testimonial_consent = 'granted';
                localStorage.setItem('aura_user_data', JSON.stringify(currentStats));
                window.dispatchEvent(new CustomEvent('aura_stats_updated'));
            } catch (e) {}
            
            setIsVisible(false);

        } catch (error: any) {
            toast.dismiss('claim-testimonial');
            toast.error('Ocurrió un error al reclamar tu recompensa. Inténtalo de nuevo más tarde.');
            setIsClaiming(false);
        }
    };

    const handleDecline = async () => {
        setIsVisible(false);
        if (!user) return;
        
        try {
             // 1. Update local immediately
             const currentStats = JSON.parse(localStorage.getItem('aura_user_data') || '{}');
             currentStats.pending_testimonial_request = false;
             currentStats.testimonial_consent = 'rejected';
             localStorage.setItem('aura_user_data', JSON.stringify(currentStats));
             window.dispatchEvent(new CustomEvent('aura_stats_updated'));
             
             // 2. Sync to backend
             await supabase.from('profiles').update({
                  stats: currentStats
             }).eq('id', user.id);
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <AnimatePresence>
            {isVisible && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    {/* Dark overlay backdrop */}
                    <motion.div 
                        initial={{ opacity: 0, backdropFilter: "blur(0px)" }}
                        animate={{ opacity: 1, backdropFilter: "blur(20px)" }}
                        exit={{ opacity: 0, backdropFilter: "blur(0px)" }}
                        className="absolute inset-0 bg-black/80"
                    />

                    {/* Modal Content */}
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        className="bg-black border border-white/20 rounded-3xl p-6 md:p-8 max-w-sm w-full relative overflow-hidden shadow-[0_0_100px_rgba(255,255,255,0.05)]"
                    >
                        {/* Shimmer/gradient effects */}
                        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/20 blur-[100px] pointer-events-none" />
                        
                        {/* Elite Badge Header */}
                        <div className="flex justify-center mb-6 relative z-10">
                            <div className="relative">
                                <div className="absolute inset-0 bg-primary/30 blur-xl rounded-full" />
                                <div className="size-20 bg-gradient-to-br from-yellow-300 via-primary to-orange-600 rounded-full flex items-center justify-center border-4 border-black relative z-10">
                                    <Crown size={32} className="text-black fill-black" />
                                </div>
                                <div className="absolute -bottom-2 -right-2 bg-black border border-white/20 size-8 rounded-full flex items-center justify-center z-20">
                                    <Camera size={14} className="text-white" />
                                </div>
                            </div>
                        </div>

                        {/* Title */}
                        <h2 className="text-2xl font-black uppercase tracking-tighter text-center text-white mb-4 relative z-10">
                            {t('testimonial.title_prefix')}<span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-orange-400">{t('testimonial.title_highlight')}</span>
                        </h2>

                        {/* Text */}
                        <div className="space-y-4 mb-8 relative z-10 text-center text-sm">
                            <p className="text-white/80 leading-relaxed font-medium">
                                {t('testimonial.desc1')}
                            </p>
                            <p className="text-white/60 leading-relaxed">
                                <Trans t={t} i18nKey="testimonial.desc2" components={{ strong: <strong className="text-white" /> }} />
                            </p>
                            <div className="bg-primary/10 border border-primary/30 p-4 rounded-xl mt-4 text-primary w-full">
                                <div className="flex justify-center mb-2"><Star size={20} className="fill-primary" /></div>
                                <span className="font-black uppercase tracking-widest text-[11px] block text-center">{t('testimonial.reward_vip')}</span>
                                <span className="font-bold text-center block leading-tight text-white mt-1">{t('testimonial.reward_desc')}</span>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col gap-3 relative z-10">
                            <button 
                                onClick={handleAccept}
                                disabled={isClaiming}
                                className="w-full bg-white text-black py-4 rounded-xl font-black uppercase tracking-widest text-sm flex items-center justify-center gap-2 hover:bg-gray-200 transition-colors disabled:opacity-50"
                            >
                                {isClaiming ? (
                                    <span className="material-symbols-outlined animate-spin text-xl">progress_activity</span>
                                ) : (
                                    <>
                                        {t('testimonial.btn_accept')} <Check size={18} />
                                    </>
                                )}
                            </button>
                            <button 
                                onClick={handleDecline}
                                disabled={isClaiming}
                                className="w-full bg-transparent text-white/40 py-3 rounded-xl font-bold uppercase tracking-widest text-xs flex items-center justify-center gap-2 hover:text-white/80 hover:bg-white/5 transition-colors"
                            >
                                {t('testimonial.btn_decline')} <X size={14} />
                            </button>
                            <p className="text-center text-[9px] text-white/30 uppercase mt-2 px-4 leading-tight">{t('testimonial.disclaimer')}</p>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};
